import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import DailyCcEditor from '../../components/DailyCcEditor';
import {
  UserPlus, Trash2, Loader2, Briefcase, Copy, Check, Mail,
  ClipboardList, CheckCircle, XCircle, Clock, Eye, MapPin
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminClients = () => {
  const { getAuthHeader } = useAuth();
  const [activeTab, setActiveTab] = useState('clients');
  const [clients, setClients] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [copiedPassword, setCopiedPassword] = useState(null);
  const [newClient, setNewClient] = useState({ name: '', email: '', company: '', phone: '', password: '' });
  const [createdCredentials, setCreatedCredentials] = useState(null);
  const [updatingRequest, setUpdatingRequest] = useState(null);
  // Daily-update CC management (per client)
  const [ccModalClient, setCcModalClient] = useState(null);
  const [ccList, setCcList] = useState([]);
  const [ccLoading, setCcLoading] = useState(false);
  const [ccSaving, setCcSaving] = useState(false);

  const openCcModal = async (client) => {
    setCcModalClient(client);
    setCcLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/clients/${client.id}/daily-update-cc`, getAuthHeader());
      setCcList(res.data?.cc_emails || []);
    } catch { toast.error('Failed to load CC list'); setCcList([]); }
    finally { setCcLoading(false); }
  };

  const saveCcList = async () => {
    setCcSaving(true);
    try {
      await axios.put(`${API}/api/admin/clients/${ccModalClient.id}/daily-update-cc`, { cc_emails: ccList }, getAuthHeader());
      toast.success(`Daily update CC list saved for ${ccModalClient.name}`);
      setCcModalClient(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save CC list');
    } finally { setCcSaving(false); }
  };

  const fetchClients = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/clients`, getAuthHeader());
      setClients(res.data);
    } catch { toast.error('Failed to load clients'); }
    finally { setLoading(false); }
  }, [getAuthHeader]);

  const fetchRequests = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/job-requests`, getAuthHeader());
      setRequests(res.data);
    } catch { toast.error('Failed to load job requests'); }
    finally { setRequestsLoading(false); }
  }, [getAuthHeader]);

  useEffect(() => { fetchClients(); fetchRequests(); }, [fetchClients, fetchRequests]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newClient.name || !newClient.email) { toast.error('Name and email are required'); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/api/admin/clients`, newClient, getAuthHeader());
      toast.success(res.data.message);
      setCreatedCredentials({ email: res.data.email, password: res.data.temporary_password });
      setNewClient({ name: '', email: '', company: '', phone: '', password: '' });
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create client');
    } finally { setCreating(false); }
  };

  const handleDelete = async (clientId, clientName) => {
    if (!window.confirm(`Delete client account for "${clientName}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/api/admin/clients/${clientId}`, getAuthHeader());
      toast.success('Client account deleted');
      fetchClients();
    } catch { toast.error('Failed to delete client'); }
  };

  const handleCopyPassword = (password) => {
    navigator.clipboard.writeText(password);
    setCopiedPassword(password);
    setTimeout(() => setCopiedPassword(null), 2000);
  };

  const handleUpdateRequestStatus = async (requestId, newStatus) => {
    setUpdatingRequest(requestId);
    try {
      await axios.put(`${API}/api/admin/job-requests/${requestId}/status?status=${newStatus}`, {}, getAuthHeader());
      toast.success(`Request ${newStatus}. Client notified by email.`);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally { setUpdatingRequest(null); }
  };

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  const tabs = [
    { key: 'clients', label: 'Client Accounts', icon: Briefcase },
    { key: 'requests', label: 'Job Requests', icon: ClipboardList, badge: pendingCount },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Clients</h1>
          <p className="text-sm text-gray-500 mt-1">Manage client accounts and job requests</p>
        </div>
        {activeTab === 'clients' && (
          <Button onClick={() => { setShowCreateModal(true); setCreatedCredentials(null); }} className="bg-purple-600 hover:bg-purple-500" data-testid="create-client-btn">
            <UserPlus size={16} className="mr-2" /> Create Client
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1" data-testid="client-tabs">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === tab.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            data-testid={`tab-${tab.key}`}
          >
            <tab.icon size={16} />
            {tab.label}
            {tab.badge > 0 && (
              <span className="ml-1 bg-amber-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">{tab.badge}</span>
            )}
          </button>
        ))}
      </div>

      {/* Clients Tab */}
      {activeTab === 'clients' && (
        <>
          {loading ? (
            <div className="flex justify-center p-12"><Loader2 size={28} className="animate-spin text-purple-500" /></div>
          ) : clients.length > 0 ? (
            <div className="space-y-2">
              {clients.map(client => (
                <Card key={client.id} className="p-4 border-0 shadow-sm" data-testid={`client-row-${client.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 font-bold text-sm">
                        {(client.name || '?')[0].toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{client.name}</p>
                        <div className="flex items-center gap-2 text-xs text-gray-400">
                          <Mail size={12} /> {client.email}
                          {client.company && <><span className="text-gray-300">|</span> {client.company}</>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className="bg-blue-50 text-blue-600 border border-blue-200">
                        <Briefcase size={12} className="mr-1" /> {client.job_count} jobs
                      </Badge>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openCcModal(client)}
                        className="text-purple-600 border-purple-200 hover:bg-purple-50"
                        title="Manage CC recipients for this client's daily update emails"
                        data-testid={`cc-client-${client.id}`}
                      >
                        <Mail size={14} className="mr-1" /> CC
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDelete(client.id, client.name)} className="text-red-500 border-red-200 hover:bg-red-50" data-testid={`delete-client-${client.id}`}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="p-12 border-0 shadow-sm text-center">
              <Briefcase size={40} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-400">No client accounts yet</p>
              <p className="text-xs text-gray-300 mt-1">Create a client account to give them access to the Client Portal</p>
            </Card>
          )}
        </>
      )}

      {/* Job Requests Tab */}
      {activeTab === 'requests' && (
        <>
          {requestsLoading ? (
            <div className="flex justify-center p-12"><Loader2 size={28} className="animate-spin text-purple-500" /></div>
          ) : requests.length > 0 ? (
            <div className="space-y-3">
              {requests.map(req => {
                const isPending = req.status === 'pending';
                const statusStyles = {
                  pending: 'bg-amber-100 text-amber-700',
                  reviewed: 'bg-blue-100 text-blue-700',
                  approved: 'bg-green-100 text-green-700',
                  declined: 'bg-red-100 text-red-700',
                  completed: 'bg-purple-100 text-purple-700',
                };
                return (
                  <Card key={req.id} className={`p-5 border-0 shadow-sm ${isPending ? 'ring-1 ring-amber-200' : ''}`} data-testid={`request-row-${req.id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-semibold text-gray-900">{req.title}</p>
                          <Badge className={`${statusStyles[req.status] || 'bg-gray-100 text-gray-600'} text-xs capitalize`}>
                            {(req.status || '').replace(/_/g, ' ')}
                          </Badge>
                          {req.delivery_boundary && req.delivery_boundary.length >= 3 && (
                            <Badge className="bg-purple-100 text-purple-700 text-xs"><MapPin size={10} className="mr-0.5" /> Zone</Badge>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 mb-2">
                          <span className="flex items-center gap-1"><Mail size={12} /> {req.client_name} ({req.client_email})</span>
                          {req.location && <span className="flex items-center gap-1"><MapPin size={12} /> {req.location}</span>}
                          {req.estimated_deliveries && <span>{req.estimated_deliveries} deliveries</span>}
                          <span className="flex items-center gap-1"><Clock size={12} /> {new Date(req.created_at).toLocaleDateString()}</span>
                        </div>
                        {req.description && <p className="text-sm text-gray-600 line-clamp-2">{req.description}</p>}
                        {req.notes && <p className="text-xs text-gray-400 mt-1 italic">Note: {req.notes}</p>}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {isPending && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleUpdateRequestStatus(req.id, 'reviewed')}
                              disabled={updatingRequest === req.id}
                              className="text-blue-600 border-blue-200 hover:bg-blue-50"
                              data-testid={`review-request-${req.id}`}
                            >
                              {updatingRequest === req.id ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} className="mr-1" />}
                              Review
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleUpdateRequestStatus(req.id, 'approved')}
                              disabled={updatingRequest === req.id}
                              className="bg-green-600 hover:bg-green-500 text-white"
                              data-testid={`approve-request-${req.id}`}
                            >
                              {updatingRequest === req.id ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} className="mr-1" />}
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleUpdateRequestStatus(req.id, 'declined')}
                              disabled={updatingRequest === req.id}
                              className="text-red-500 border-red-200 hover:bg-red-50"
                              data-testid={`decline-request-${req.id}`}
                            >
                              <XCircle size={14} />
                            </Button>
                          </>
                        )}
                        {req.status === 'reviewed' && (
                          <>
                            <Button size="sm" onClick={() => handleUpdateRequestStatus(req.id, 'approved')} disabled={updatingRequest === req.id} className="bg-green-600 hover:bg-green-500 text-white" data-testid={`approve-request-${req.id}`}>
                              <CheckCircle size={14} className="mr-1" /> Approve
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleUpdateRequestStatus(req.id, 'declined')} disabled={updatingRequest === req.id} className="text-red-500 border-red-200 hover:bg-red-50" data-testid={`decline-request-${req.id}`}>
                              <XCircle size={14} />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card className="p-12 border-0 shadow-sm text-center">
              <ClipboardList size={40} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-400">No job requests yet</p>
              <p className="text-xs text-gray-300 mt-1">Client-submitted job requests will appear here</p>
            </Card>
          )}
        </>
      )}

      {/* Create Client Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-md" data-testid="create-client-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus size={20} className="text-purple-600" />
              Create Client Account
            </DialogTitle>
          </DialogHeader>

          {createdCredentials ? (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm font-medium text-green-800 mb-1">Account created! A welcome email has been sent.</p>
                <p className="text-xs text-green-600 mb-3">Share these credentials with the client:</p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between bg-white rounded px-3 py-2">
                    <span className="text-gray-600">Email: <strong>{createdCredentials.email}</strong></span>
                  </div>
                  <div className="flex items-center justify-between bg-white rounded px-3 py-2">
                    <span className="text-gray-600">Password: <strong className="font-mono">{createdCredentials.password}</strong></span>
                    <button onClick={() => handleCopyPassword(createdCredentials.password)} className="text-purple-600 hover:text-purple-700">
                      {copiedPassword === createdCredentials.password ? <Check size={16} /> : <Copy size={16} />}
                    </button>
                  </div>
                </div>
                <p className="text-xs text-green-600 mt-3">Login URL: {window.location.origin}/client/login</p>
              </div>
              <Button onClick={() => setShowCreateModal(false)} className="w-full">Done</Button>
            </div>
          ) : (
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Name *</Label>
                  <Input value={newClient.name} onChange={e => setNewClient({...newClient, name: e.target.value})} placeholder="John Doe" data-testid="client-name-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Email *</Label>
                  <Input type="email" value={newClient.email} onChange={e => setNewClient({...newClient, email: e.target.value})} placeholder="client@company.com" data-testid="client-email-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Company</Label>
                  <Input value={newClient.company} onChange={e => setNewClient({...newClient, company: e.target.value})} placeholder="Acme Corp" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Phone</Label>
                  <Input value={newClient.phone} onChange={e => setNewClient({...newClient, phone: e.target.value})} placeholder="(555) 123-4567" />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Password (leave blank to auto-generate)</Label>
                <Input value={newClient.password} onChange={e => setNewClient({...newClient, password: e.target.value})} placeholder="Auto-generated if empty" />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" type="button" onClick={() => setShowCreateModal(false)}>Cancel</Button>
                <Button type="submit" disabled={creating} className="bg-purple-600 hover:bg-purple-500" data-testid="create-client-submit">
                  {creating ? <Loader2 size={14} className="mr-2 animate-spin" /> : <UserPlus size={14} className="mr-2" />}
                  Create Account
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Daily-update CC recipients dialog */}
      <Dialog open={!!ccModalClient} onOpenChange={() => !ccSaving && setCcModalClient(null)}>
        <DialogContent data-testid="cc-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail size={18} className="text-purple-600" /> Daily Update CC Recipients
            </DialogTitle>
          </DialogHeader>
          {ccModalClient && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                These addresses are CC'd on <span className="font-semibold">{ccModalClient.name}</span>'s
                daily delivery update emails (sent to {ccModalClient.email}). They do not receive other emails.
              </p>
              {ccLoading ? (
                <div className="flex justify-center py-6"><Loader2 size={22} className="animate-spin text-purple-500" /></div>
              ) : (
                <DailyCcEditor ccList={ccList} onChange={setCcList} disabled={ccSaving} />
              )}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setCcModalClient(null)} disabled={ccSaving} data-testid="cc-cancel-btn">
                  Cancel
                </Button>
                <Button onClick={saveCcList} disabled={ccSaving || ccLoading} className="bg-purple-600 hover:bg-purple-700" data-testid="cc-save-btn">
                  {ccSaving && <Loader2 size={14} className="mr-1.5 animate-spin" />}
                  Save CC List
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminClients;
